import { ConfigProvider, ThemeConfig, getThemeConfig, theme } from '@umu/design-base';
import { langVal } from '@umu/ukit-lang';
import { getRootScale } from 'businessCommon/src/@utils/fontSize';
import cloneDeep from 'lodash/cloneDeep';
import React, { memo, useEffect, useMemo, useState, PropsWithChildren } from 'react';
import { THEME_TYPE, type BaseToken, type ThemeConfigData } from './themeConfigConst';
import {
    defaultSystemTextColor,
    defaultSystemThemeColor,
    defaultSystemThemeConfig,
    getSystemThemeConfig,
} from './utils';
export type { ThemeConfigData };

interface ThemeConfigProviderProps {
    userThemeConfig?: ThemeConfigData;
    /** 用于 antd 全局变量注入的根类名 */
    rootClassNameKey?: string;
}
type ThemeConfigType = typeof ThemeConfig;

type ThemeConfigGlobalTokenKey = keyof NonNullable<ThemeConfigType['token']>;

const CssVarPrefix = 'umu';

const getDarkThemeConfig = (config: ThemeConfigType): ThemeConfigType => {
    const darkThemeConfig: ThemeConfigType = cloneDeep(config);
    if (darkThemeConfig.token) {
        const removeTokenFields: ThemeConfigGlobalTokenKey[] = [
            'colorText',
            'colorTextBase',
            'colorBorder',
            'colorBorderSecondary',
            'colorFill',
            'colorFillSecondary',
            'colorFillTertiary',
            'colorFillQuaternary',
        ];

        removeTokenFields.forEach((key) => {
            if (darkThemeConfig.token && darkThemeConfig.token[key]) {
                delete darkThemeConfig.token[key];
            }
        });

        darkThemeConfig.token = {
            ...darkThemeConfig.token,
            colorBgBase: '#1e2022',
            colorBorder: '#666666',
            colorBorderSecondary: '#424141',
            colorBgLayout: '#313438',
        };

        darkThemeConfig.components = {
            ...darkThemeConfig.components,
            Tabs: {
                colorText: '#fff',
                itemActiveColor: '#fff',
                itemColor: '#fff',
                itemHoverColor: '#fff',
                itemSelectedColor: '#fff',
            },
        };
        darkThemeConfig.algorithm = theme.darkAlgorithm;
    }

    return darkThemeConfig;
};

const defaultColorPrimary = ThemeConfig.token!.colorPrimary! || defaultSystemThemeColor;
const defaultColorTextBase = ThemeConfig.token!.colorTextBase! || defaultSystemTextColor;

const defaultToken: BaseToken = {
    colorPrimary: defaultColorPrimary,
    colorText: defaultColorTextBase,
};

const normalFontFamily = `"Helvetica Neue", Helvetica, Arial, "PingFang SC", "Hiragino Sans GB", "Heiti SC", "Microsoft YaHei", "WenQuanYi Micro Hei", sans-serif`;
const jpFontFamily = `Meiryo, 'Yu gothic', noto_sansregular, 'hiragino kaku gothic pro', 'MS Gothic', meiryo, sans-serif, noto_sansregular, Arial, 'Microsoft Yahei', 'Hiragino Sans GB'`;

export const defaultThemeConfig = {
    type: THEME_TYPE.DEFAULT,
    token: defaultToken,
};

export const getThemeConfigData = (userThemeConfig?: ThemeConfigData): ThemeConfigData => {
    const { type, token } = userThemeConfig ?? defaultSystemThemeConfig;

    return {
        type,
        token,
    };
};

const ThemeConfigProvider: React.FC<PropsWithChildren<ThemeConfigProviderProps>> = (props) => {
    const [themeConfig, setThemeConfig] = useState<ThemeConfigData>(getSystemThemeConfig());

    useEffect(() => {
        // 非自定义的情况
        if (!props.userThemeConfig) {
            window.addEventListener('message', function (e) {
                if (e.origin === 'http://design.umu.work' && e.data) {
                    setThemeConfig({
                        type: THEME_TYPE.DEFAULT,
                        token: {
                            colorPrimary: e.data.token.colorPrimary,
                            colorText: e.data.token.colorText,
                        },
                    });
                }
            });
        }
    }, [props.userThemeConfig]);

    // TODO 暂时使用多语的类名作为 antd 全局变量注入的根
    const key = useMemo(() => props.rootClassNameKey || `locale-${langVal}`, [props.rootClassNameKey, langVal]);

    const finialConfig: ThemeConfigType = useMemo(() => {
        const fontFamily = langVal === 'jp' ? jpFontFamily : normalFontFamily;

        if (props.userThemeConfig) {
            const { token } = props.userThemeConfig;
            const primaryColor = token?.colorPrimary ?? defaultColorPrimary;
            const colorText = token?.colorText ?? defaultColorTextBase;
            const config = getThemeConfig(primaryColor, colorText, getRootScale());

            return {
                ...config,
                token: {
                    ...config.token,
                    fontFamily,
                },
            };
        } else {
            const primaryColor = themeConfig?.token?.colorPrimary ?? defaultColorPrimary;
            const colorText = themeConfig?.token?.colorText ?? defaultColorTextBase;
            const config = getThemeConfig(primaryColor, colorText, getRootScale());
            const currentThemeConfig = {
                ...config,
                token: {
                    ...config.token,
                    fontFamily,
                },
            };
            if (themeConfig?.type === THEME_TYPE.DARK) {
                return getDarkThemeConfig(currentThemeConfig);
            }
            return currentThemeConfig;
        }
    }, [props.userThemeConfig, themeConfig, langVal]);

    return (
        <ConfigProvider
            theme={{
                ...finialConfig,
                cssVar: { key, prefix: CssVarPrefix },
                hashed: false,
            }}
            // 会自动在按钮内容为两个汉字的时候加空格，需要关掉
            button={{ autoInsertSpace: false } as any}
            wave={{ disabled: true }}
        >
            {props.children}
        </ConfigProvider>
    );
};

export default memo(ThemeConfigProvider);
